import { Button, Dropdown, Typography } from "antd";
import { FaChevronDown } from "react-icons/fa";
import { TbCloudUpload, TbFileAnalytics } from "react-icons/tb";
import { ALL_BATCHES } from "../../../../entities/entities";
import ActionButton from "../../../masterComponents/actionButton";
import Icon from "../../../masterComponents/Icon";
import { InboxOutlined, ClearOutlined } from "@ant-design/icons";
import {
  BatchButton,
  CustomTooltip,
  HeaderTitle,
  RemappingHeader,
  RemappingPage,
  Sidebar,
  StatusBar,
  StatusFile,
  StatusFileName,
  StatusPanel,
  StatusRow,
  UploadArea,
  Workflow,
  WorkflowRow,
} from "../remapping.style";
import type { RemappingUploadAreaType } from "./RemappingUploadAreaType";
const { Title, Text } = Typography;

const RemappingUploadArea: React.FC<RemappingUploadAreaType> = ({
  selectedBatch,
  setSelectedBatch,
  reusedFileName,
  setReusedFileName,
  reusedS3Key,
  setReusedS3Key,
  selectedFile,
  setSelectedFile,
  fetchRemappingUploadResponse,
  remappingUploadResponse,
  isUploading,
  clearWorkflowState,
  remappingJob,
  isJobLoading,
  remappingJobData,
  remappingPreview,
  isRemappingPreviewLoading,
  remappingPreviewUrl,
}) => {

  const clickToUploadFile = () => {
    const formData = new FormData();
    if (selectedFile) {
      formData.append("file", selectedFile);
    } else if (reusedS3Key) {
      formData.append("s3_key", reusedS3Key);
    } else {
      return;
    }
    fetchRemappingUploadResponse({ batch: selectedBatch }, formData);
  };

  const handleBatchChange = (newBatch: string) => {
    if (newBatch === selectedBatch) return;

    clearWorkflowState();
    setSelectedBatch(newBatch);
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setReusedFileName(null);
    setReusedS3Key(null);
    clearWorkflowState();
  };

  return (
    <RemappingPage>
      <RemappingHeader>
        <HeaderTitle>
          <Title level={4} style={{ margin: 0 }}>
            Remapping
          </Title>
          <Text type="secondary">Upload a file and apply remapping.</Text>
        </HeaderTitle>
        <BatchButton>
          <Dropdown
            overlayStyle={{ zIndex: 900 }}
            menu={{
              items: ALL_BATCHES,
              onClick: (info) => handleBatchChange(info.key),
              selectedKeys: [selectedBatch],
            }}
            trigger={["click"]}
          >
            <Button icon={<Icon icon={FaChevronDown} />} className="btn-color">
              {selectedBatch}
            </Button>
          </Dropdown>
        </BatchButton>
      </RemappingHeader>

      <Workflow>
        <WorkflowRow>
          <UploadArea
            maxCount={1}
            showUploadList={false}
            beforeUpload={(file) => {
              setSelectedFile(file);
              setReusedFileName(null);
              setReusedS3Key(null);
              clearWorkflowState();
              return false;
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Click or drag file here</p>
          </UploadArea>

          <Sidebar>
            <StatusBar>
              <StatusPanel>
                <StatusRow>
                  <Text type="secondary">File</Text>
                  <StatusFile>
                    <CustomTooltip
                      title={selectedFile?.name ?? reusedFileName}
                      color="#f5f5f5"
                    >
                      <StatusFileName>
                        {selectedFile?.name ?? reusedFileName ?? "—"}
                      </StatusFileName>
                    </CustomTooltip>
                  </StatusFile>
                </StatusRow>
              </StatusPanel>
              <ActionButton
                className="primary-btn-color"
                disabled={!selectedFile && !reusedFileName}
                styles={{ height: 40 }}
                onClick={handleClearFile}
                icon={<Icon icon={ClearOutlined} />}
              />
            </StatusBar>
            <ActionButton
              className="primary-btn-color"
              text="Upload file"
              icon={<Icon icon={TbCloudUpload} size={20} />}
              iconPosition="end"
              styles={{ width: "100%" }}
              disabled={!selectedFile && !reusedFileName}
              onClick={clickToUploadFile}
              loading={isUploading}
            />
            <ActionButton
              text="Analyze remapping"
              className="primary-btn-color"
              iconPosition={"end"}
              loading={isRemappingPreviewLoading}
              icon={<Icon icon={TbFileAnalytics} size={24} />}
              disabled={
                isRemappingPreviewLoading ||
                !remappingUploadResponse?.success ||
                remappingJobData?.success
              }
              onClick={remappingPreview}
            />
            <ActionButton
              className="primary-btn-color"
              text="Start remapping"
              styles={{ width: "100%" }}
              disabled={
                !remappingUploadResponse?.success ||
                remappingJobData?.success ||
                !remappingPreviewUrl
              }
              onClick={remappingJob}
              loading={isJobLoading}
            />
          </Sidebar>
        </WorkflowRow>
      </Workflow>
    </RemappingPage>
  );
};

export default RemappingUploadArea;
